import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { HybridIcon } from '../components/HybridIcon';
import type { Car } from '../models';
import './Cars.css';

function getStatusMeta(status: Car['status']) {
  switch (status) {
    case 'available':
      return { icon: 'checkmark-circle', label: 'Available', pillClass: 'pill-good' };
    case 'rented':
      return { icon: 'key', label: 'Rented', pillClass: 'pill-accent' };
    case 'maintenance':
      return { icon: 'construct', label: 'Maintenance', pillClass: 'pill-danger' };
    default:
      return { icon: 'help-circle', label: status, pillClass: 'pill-accent' };
  }
}

function CarRow({ car, serviceDue, onPress }: { car: Car; serviceDue: boolean; onPress: () => void }) {
  const status = getStatusMeta(car.status);

  return (
    <button type="button" className="cars-row lrow" onClick={onPress}>
      <div className="car-thumb cars-row-thumb">
        <HybridIcon name="car-sport" size={20} color="currentColor" />
      </div>
      <div className="cars-row-main">
        <div className="cars-row-heading">
          <h3 className="cars-row-title">{car.licensePlate || `${car.make} ${car.model}`}</h3>
          <span className={`pill ${status.pillClass}`}>
            <HybridIcon name={status.icon} size={14} color="currentColor" />
            {status.label}
          </span>
        </div>
        <p className="cars-row-subtitle">{car.year} {car.make} {car.model}</p>
        <div className="cars-row-meta">
          <span>
            <HybridIcon name="speedometer" size={14} color="currentColor" />
            {car.mileage.toLocaleString()} km
          </span>
          <span>
            <HybridIcon name="card" size={14} color="currentColor" />
            {car.vin || 'VIN not added'}
          </span>
        </div>
      </div>
      <div className="cars-row-side">
        {serviceDue ? <span className="pill pill-warn">Service due</span> : null}
        <HybridIcon name="chevron-forward" size={18} color="currentColor" />
      </div>
    </button>
  );
}

export default function Cars() {
  const { state } = useAppContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const serviceDueIds = useMemo(() => {
    const due = new Set<string>();
    state.cars.forEach((car) => {
      const logs = state.services.filter((service) => service.carId === car.id);
      let nextKm: number | undefined;
      for (const log of logs) {
        const candidate =
          typeof log.nextServiceKm === 'number' && log.nextServiceKm > 0
            ? log.nextServiceKm
            : log.mileage
              ? Math.ceil(log.mileage / 5000) * 5000
              : undefined;
        if (candidate && (!nextKm || candidate > nextKm)) {
          nextKm = candidate;
        }
      }
      if (nextKm !== undefined && car.mileage >= nextKm) {
        due.add(car.id);
      }
    });
    return due;
  }, [state.cars, state.services]);

  const cars = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return state.cars;
    }

    return state.cars.filter((car) => {
      const year = String(car.year ?? '');
      return (
        car.make.toLowerCase().includes(normalizedQuery) ||
        car.model.toLowerCase().includes(normalizedQuery) ||
        (car.licensePlate || '').toLowerCase().includes(normalizedQuery) ||
        (car.vin || '').toLowerCase().includes(normalizedQuery) ||
        year.includes(normalizedQuery)
      );
    });
  }, [query, state.cars]);

  const stats = useMemo(
    () => ({
      total: state.cars.length,
      available: state.cars.filter((car) => car.status === 'available').length,
      maintenance: state.cars.filter((car) => car.status === 'maintenance').length,
      due: serviceDueIds.size,
    }),
    [serviceDueIds.size, state.cars],
  );

  return (
    <div className="cars-page">
      <section className="cars-hero card">
        <div>
          <p className="eyebrow">Fleet</p>
          <h1 className="cars-title">Vehicles ready, booked, and waiting on service.</h1>
          <p className="cars-subtitle">
            Scan the fleet quickly, then drill into a vehicle when you need history or availability.
          </p>
        </div>
        <button type="button" className="btn btn-primary btn-lg" onClick={() => navigate('/cars/new')}>
          <HybridIcon name="add-circle" size={20} color="currentColor" />
          <span>Add New Car</span>
        </button>
      </section>

      <section className="cars-stats-grid">
        <div className="metric">
          <span>Total fleet</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="metric">
          <span>Available now</span>
          <strong>{stats.available}</strong>
        </div>
        <div className="metric">
          <span>In maintenance</span>
          <strong>{stats.maintenance}</strong>
        </div>
        <div className="metric">
          <span>Service due</span>
          <strong>{stats.due}</strong>
        </div>
      </section>

      <section className="cars-toolbar card">
        <div className="cars-search-container">
          <HybridIcon name="search" size={18} color="currentColor" className="cars-search-icon" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by make, model, plate, VIN, or year"
            className="cars-search-input"
          />
          {query ? (
            <button className="cars-clear-button" onClick={() => setQuery('')} type="button" aria-label="Clear search">
              <HybridIcon name="close-circle" size={18} color="currentColor" />
            </button>
          ) : null}
        </div>
        <span className="pill pill-accent">{cars.length} matching</span>
      </section>

      {cars.length === 0 ? (
        <section className="cars-empty card">
          <div className="car-thumb lg">
            <HybridIcon name="car-outline" size={26} color="currentColor" />
          </div>
          <h2>No cars found</h2>
          <p>Try a different search, or add your first vehicle to start tracking the fleet.</p>
        </section>
      ) : (
        <section className="cars-list">
          {cars.map((car) => (
            <CarRow
              key={car.id}
              car={car}
              serviceDue={serviceDueIds.has(car.id)}
              onPress={() => navigate(`/cars/${car.id}`)}
            />
          ))}
        </section>
      )}
    </div>
  );
}
